import React, { useContext } from "react";
import { useState, useEffect } from "react";
import { Printer, Share2, HelpCircle } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import GlobalContext from "../../context/GlobalContext";
import { MdKeyboardArrowDown } from "react-icons/md";
import ProfileDropdown from "../../components/shared/profileDropdown";
import {
  FileText,
  Heart,
  Pill,
  AlertTriangle,
  Stethoscope,
} from "lucide-react";
import Navbar from "../../components/shared/Navbar";

function parseSummaryToSections(summary) {
  if (!summary) return [];

  const sectionRegex =
    /(?:\*\*(.*?)\*\*|^#{2,4}\s*(.*?))(?:\n+([\s\S]*?))(?=(\*\*|#{2,4}|$))/gm;

  const sections = [];
  let match;

  while ((match = sectionRegex.exec(summary)) !== null) {
    let rawTitle = (match[1] || match[2] || "").trim().replace(/:$/, "");

    // 🚀 strip leftover "#" characters
    rawTitle = rawTitle.replace(/^#+\s*/, "");

    const content = (match[3] || "").trim();

    if (rawTitle) {
      sections.push({ title: rawTitle, content });
    }
  }

  return sections;
}

export default function MedicalSummary() {
  const [currentTime, setCurrentTime] = useState("");
  const [sections, setSections] = useState([]);
  const { patientId } = useParams();
  const { userInfo } = useContext(GlobalContext);

  const sectionConfig = {
    "Current Medical Conditions": {
      icon: Heart,
      description: "Active medical conditions and ongoing treatments",
    },
    "Past Medical History": {
      icon: FileText,
      description: "Medical events or illnesses in the past",
    },
    Medications: {
      icon: Pill,
      description: "List of prescribed medications",
    },
    Allergies: {
      icon: AlertTriangle,
      description: "Known allergies and sensitivities",
    },
    "Additional Categories": {
      icon: Stethoscope,
      description: "Other social, lifestyle, or family health information",
    },
  };

  useEffect(() => {
    // Get current time of day for greeting
    const hour = new Date().getHours();
    if (hour < 12) setCurrentTime("Morning");
    else if (hour < 18) setCurrentTime("Afternoon");
    else setCurrentTime("Evening");
    fetchUserSummary();
  }, [patientId]);

  async function fetchUserSummary() {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}/api/summary/?user=${patientId}`
    );

    console.log("API raw response:", response.data);
    const rawSummary = response.data[0]?.summary;
    console.log("Raw summary text:", rawSummary);

    const parsedSections = rawSummary ? parseSummaryToSections(rawSummary) : [];
    console.log("Parsed sections:", parsedSections);

    setSections(parsedSections);
  }

  return (
    <>
      <Navbar />
      <div className="mt-10 px-4 sm:px-10 md:px-20 w-full">
        <section className="space-y-4">
          <div className="text-center mb-10 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-3">
              Patient{" "}
              <span className="text-slate-400 font-normal">Summary</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-green-600 mx-auto mb-6"></div>
          </div>

          <div className="grid gap-6 sm:gap-8">
            {sections.map((section, idx) => {
              const config = sectionConfig[section.title] || {
                icon: FileText,
                description: "General medical information",
              };
              const IconComponent = config.icon;

              return (
                <div key={idx} className="group">
                  <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300">
                    {/* Section Header */}
                    <div className="bg-gradient-to-r from-slate-50 to-green-50 px-6 sm:px-8 py-4 sm:py-5 border-b border-slate-100">
                      <div className="flex items-center gap-4">
                        <div className="bg-green-100 p-3 rounded-xl group-hover:bg-green-200 transition-colors">
                          <IconComponent className="w-5 h-5 text-green-700" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-1">
                            {section.title}
                          </h3>
                          <p className="text-sm text-slate-500 hidden sm:block">
                            {config.description}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Section Content */}
                    <div className="px-6 sm:px-8 py-6 sm:py-7">
                      <p className="text-slate-700 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                        {section.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* <div className="flex items-center gap-2 text-gray-600">
            <HelpCircle className="w-5 h-5" />
            <p>Need help understanding this summary?</p>
          </div> */}

          {/* <div className="flex justify-end gap-4 pt-4">
          <Button
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100 border-gray-300 text-gray-700"
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>
          <Button className="flex items-center gap-2 bg-[#4D8D6E] hover:bg-[#3d7057] text-white">
            <Printer className="w-4 h-4" />
            Print
          </Button>
        </div>*/}
        </section>

        {/* <div className="flex justify-center gap-4 pt-4">
        <Button
          variant="outline"
          className="flex flex-1 justify-center items-center gap-2 hover:bg-[#f0f9f5] border-[#4D8D6E] text-[#4D8D6E]"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </Button>
        <Button className="flex-1 bg-[#4D8D6E] hover:bg-[#3d7057] text-white">
          View Full Records
        </Button>
      </div> */}
      </div>
    </>
  );
}
